/**
 * DashboardPage — The main signed-in home page.
 *
 * Five-tab workflow:
 *   Tab 1: "Voting"       — This week's meals. All users vote here.
 *          Primary users can regenerate (random) or manually choose meals.
 *   Tab 2: "Results"      — Vote tally + finalization. (Primary users only)
 *   Tab 3: "Grocery List"  — Generated grocery list with store integrations. (Primary users only)
 *   Tab 4: "Calendar"     — Drag-and-drop meal scheduling. (All users)
 *   Tab 5: "My Recipes"   — Full recipe library with add/edit/delete. (All users)
 *
 * Members (non-primary) only see Tabs 1, 4, and 5.
 */

import GroceryTab from '../../components/grocery/GroceryTab';
import CalendarPlannerTab from '../../components/calendar/CalendarPlannerTab';
import RecipesPage from '../app/RecipesPage';
import OnboardingTooltip from '../../components/common/OnboardingTooltip';
import '../../styles/grocery-v2.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useHousehold } from '../../hooks/useHousehold';
import { useApi } from '../../hooks/useApi';
import { getWeekId, formatWeekRange } from '@tabletryb/shared';
import {
  ThumbsUp, ThumbsDown, ChefHat, ShoppingCart,
  ListChecks, Check, RefreshCw, Shuffle, BookOpen,
  Search, X, Loader, CalendarDays,
} from 'lucide-react';

type TabId = 'selections' | 'results' | 'grocery' | 'calendar' | 'recipes';

interface RecipeRef {
  recipeId: string;
  title: string;
  image: string;
  readyInMinutes: number;
  servings: number;
  dishTypes: string[];
}

interface Meal {
  mealId: string;
  recipe: RecipeRef;
  addedAt: string;
}

interface Voter {
  userId: string;
  displayName: string;
  vote: 'up' | 'down' | null;
}

interface VoteSummary {
  mealId: string;
  recipeId: string;
  recipeTitle: string;
  recipeImage: string;
  readyInMinutes: number;
  servings: number;
  upVotes: number;
  downVotes: number;
  score: number;
  voters: Voter[];
}

// ============================================================================
// Reusable Confirm Modal (replaces window.confirm)
// ============================================================================
const ConfirmModal: React.FC<{
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmLabel?: string;
}> = ({ message, onConfirm, onCancel, confirmLabel = 'Confirm' }) => (
  <div className="recipe-modal-overlay" onClick={onCancel}>
    <div
      className="recipe-modal"
      onClick={(e) => e.stopPropagation()}
      style={{ maxWidth: 420, overflow: 'visible' }}
    >
      <div style={{ padding: '28px 24px 12px', textAlign: 'center' }}>
        <p style={{ fontSize: '16px', lineHeight: 1.6, color: 'var(--color-text)' }}>{message}</p>
      </div>
      <div style={{
        display: 'flex', gap: '10px', justifyContent: 'flex-end',
        padding: '12px 24px 24px',
      }}>
        <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </div>
  </div>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isPrimary = user?.role === 'primary';
  const { household } = useHousehold();
  const [activeTab, setActiveTab] = useState<TabId>('selections');

  const tabs: { id: TabId; label: string; icon: React.ReactNode; primaryOnly: boolean }[] = [
    // Tab label is "Voting" (user-facing) but id remains 'selections' (internal routing key)
    { id: 'selections', label: 'Voting', icon: <ThumbsUp size={18} />, primaryOnly: false },
    { id: 'results', label: 'Results', icon: <ListChecks size={18} />, primaryOnly: true },
    { id: 'grocery', label: 'Grocery List', icon: <ShoppingCart size={18} />, primaryOnly: true },
    { id: 'calendar', label: 'Calendar', icon: <CalendarDays size={18} />, primaryOnly: false },
    { id: 'recipes', label: 'My Recipes', icon: <BookOpen size={18} />, primaryOnly: false },
  ];

  const visibleTabs = tabs.filter((t) => !t.primaryOnly || isPrimary);

  return (
    <div className="dashboard">
      <div className="dashboard-tabs">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            className={`dashboard-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>
      <div className="dashboard-content">
        {activeTab === 'selections' && <SelectionsTab householdName={household?.name} />}
        {activeTab === 'results' && isPrimary && <ResultsTab />}
        {activeTab === 'grocery' && isPrimary && <GroceryTab />}
        {activeTab === 'calendar' && <CalendarPlannerTab />}
        {activeTab === 'recipes' && <RecipesPage />}
      </div>
    </div>
  );
};

// ============================================================================
// Tab 1: Selections — Voting interface (all users)
// ============================================================================
const SelectionsTab: React.FC<{ householdName?: string }> = ({ householdName }) => {
  const { user } = useAuth();
  const api = useApi();
  const isPrimary = user?.role === 'primary';
  const weekId = getWeekId();

  const [meals, setMeals] = useState<Meal[]>([]);
  const [votes, setVotes] = useState<Record<string, 'up' | 'down' | null>>({});
  const [allVoters, setAllVoters] = useState<Record<string, Voter[]>>({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState<Record<string, boolean>>({});
  const [showManualPicker, setShowManualPicker] = useState(false);
  const [weekStatus, setWeekStatus] = useState<string>('none');
  const householdId = user?.householdId;

  // Confirm modal state (replaces window.confirm)
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; action: () => void } | null>(null);

  const loadData = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const [mealData, voteData] = await Promise.all([
        api.get<{ weekId: string; status: string; meals: Meal[] }>(`/v1/households/${householdId}/meal-plans/${weekId}`),
        api.get<{ votes: VoteSummary[]; allVoted: boolean }>(`/v1/households/${householdId}/meal-plans/${weekId}/votes`),
      ]);
      setMeals(mealData.meals || []);
      setWeekStatus(mealData.status);
      const myVotes: Record<string, 'up' | 'down' | null> = {};
      const votersByMeal: Record<string, Voter[]> = {};
      for (const vs of (voteData.votes || [])) {
        votersByMeal[vs.mealId] = vs.voters;
        const myVote = vs.voters.find(v => v.userId === user?.userId);
        if (myVote) myVotes[vs.mealId] = myVote.vote;
      }
      setVotes(myVotes);
      setAllVoters(votersByMeal);
    } catch (err) { console.error('Failed to load meal data:', err); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [householdId, weekId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVote = async (mealId: string, vote: 'up' | 'down' | null) => {
    if (!householdId || votingInProgress[mealId]) return;
    setVotingInProgress((prev) => ({ ...prev, [mealId]: true }));
    const currentVote = votes[mealId];
    const newVote = currentVote === vote ? null : vote;
    setVotes((prev) => ({ ...prev, [mealId]: newVote }));
    try {
      await api.post(`/v1/households/${householdId}/meal-plans/${weekId}/vote`, { mealId, vote: newVote });
      const voteData = await api.get<{ votes: VoteSummary[] }>(`/v1/households/${householdId}/meal-plans/${weekId}/votes`);
      const votersByMeal: Record<string, Voter[]> = {};
      const myVotes: Record<string, 'up' | 'down' | null> = {};
      for (const vs of (voteData.votes || [])) {
        votersByMeal[vs.mealId] = vs.voters;
        const myVote = vs.voters.find(v => v.userId === user?.userId);
        if (myVote) myVotes[vs.mealId] = myVote.vote;
      }
      setVotes(myVotes);
      setAllVoters(votersByMeal);
    } catch (err) {
      setVotes((prev) => ({ ...prev, [mealId]: currentVote }));
      console.error('Vote failed:', err);
    } finally { setVotingInProgress((prev) => ({ ...prev, [mealId]: false })); }
  };

  const doRegenerate = async () => {
    if (!householdId || generating) return;
    setGenerating(true);
    try { await api.post(`/v1/households/${householdId}/meal-plans/generate`, {}); await loadData(); }
    catch (err) { console.error('Generate failed:', err); }
    finally { setGenerating(false); }
  };

  const handleRegenerate = () => {
    if (!householdId || generating) return;
    if (meals.length > 0) {
      const isFinalized = weekStatus === 'finalized';
      setPendingConfirm({
        message: isFinalized
          ? 'This will start a new voting cycle — the current finalized meals and grocery list will be replaced. Continue?'
          : 'This will replace the current selections and clear all votes. Continue?',
        action: doRegenerate,
      });
    } else {
      doRegenerate();
    }
  };

  const handleManualSubmit = async (recipeIds: string[]) => {
    if (!householdId || generating) return;
    setGenerating(true);
    setShowManualPicker(false);
    try { await api.post(`/v1/households/${householdId}/meal-plans/generate`, { includeRecipeIds: recipeIds }); await loadData(); }
    catch (err) { console.error('Manual generate failed:', err); }
    finally { setGenerating(false); }
  };

  if (loading) return (<div className="tab-panel" style={{ textAlign: 'center', padding: '3rem' }}><Loader size={24} className="spin" /><p>Loading this week's meals...</p></div>);

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <div>
          <h2>This Week's Options</h2>
          <p className="tab-subtitle">{formatWeekRange(weekId)} — Vote on the meals you'd like this week.</p>
        </div>
        {isPrimary && (
          <div className="tab-header-action" style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRegenerate} disabled={generating} title="Randomly select meals from your recipe library">
              {generating ? <Loader size={16} className="spin" /> : <Shuffle size={16} />} Random
            </button>
            <button className="btn btn-secondary" onClick={() => {
              if (weekStatus === 'finalized') {
                setPendingConfirm({
                  message: 'This will start a new voting cycle — the current finalized meals and grocery list will be replaced. Continue?',
                  action: () => setShowManualPicker(true),
                });
              } else {
                setShowManualPicker(true);
              }
            }} disabled={generating} title="Choose specific meals from your recipe library">
              <BookOpen size={16} /> Manual Selection
            </button>
          </div>
        )}
      </div>

      {weekStatus === 'finalized' && (<div className="info-banner info-banner--success"><Check size={16} /> This week's meals have been finalized.</div>)}

      {isPrimary && (
        <OnboardingTooltip
          id="voting-regenerate"
          message="Use 'Random' to pull meals from your recipe library, or 'Manual Selection' to pick specific recipes. You can regenerate at any time — even after finalizing."
        />
      )}

      {meals.length === 0 && !generating ? (
        <div className="empty-state">
          <ChefHat size={48} /><h3>No meals selected yet</h3>
          <p>{isPrimary ? 'Generate random selections from your recipe library, or manually choose meals for the family to vote on.' : 'The primary user hasn\'t generated this week\'s selections yet. Check back soon!'}</p>
          {isPrimary && (<button className="btn btn-primary" onClick={handleRegenerate} disabled={generating}><Shuffle size={16} /> Generate Selections</button>)}
        </div>
      ) : (
        <div className="meals-grid">
          {meals.map((meal) => {
            const myVote = votes[meal.mealId];
            return (
              <div key={meal.mealId} className="meal-card">
                <div className="meal-card-image">
                  {meal.recipe.image ? (<img src={meal.recipe.image} alt={meal.recipe.title} loading="lazy" />) : (
                    <div style={{ width: '100%', height: '100%', background: 'var(--color-bg-alt, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>🍽️</div>
                  )}
                </div>
                <div className="meal-card-body">
                  <h3 className="meal-card-title">{meal.recipe.title}</h3>
                  <div className="meal-card-meta">
                    {meal.recipe.readyInMinutes > 0 && <span>⏱ {meal.recipe.readyInMinutes} min</span>}
                    {meal.recipe.servings > 0 && <span>👥 {meal.recipe.servings} servings</span>}
                  </div>
                  {weekStatus !== 'finalized' && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className={`btn ${myVote === 'up' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', justifyContent: 'center' }} onClick={() => handleVote(meal.mealId, 'up')} disabled={!!votingInProgress[meal.mealId]}>
                        👍 {myVote === 'up' ? 'Voted Yes' : 'Yes'}
                      </button>
                      <button className={`btn ${myVote === 'down' ? 'btn-primary' : 'btn-secondary'}`} style={{ flex: 1, padding: '6px', fontSize: '0.85rem', justifyContent: 'center' }} onClick={() => handleVote(meal.mealId, 'down')} disabled={!!votingInProgress[meal.mealId]}>
                        👎 {myVote === 'down' ? 'Voted No' : 'No'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {showManualPicker && <ManualMealPicker onSubmit={handleManualSubmit} onClose={() => setShowManualPicker(false)} />}

      {/* Confirm modal */}
      {pendingConfirm && (
        <ConfirmModal
          message={pendingConfirm.message}
          confirmLabel="Continue"
          onConfirm={() => { setPendingConfirm(null); pendingConfirm.action(); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Manual Meal Picker
// ============================================================================
interface RecipeItem { recipeId: string; title: string; image?: string; readyInMinutes?: number; servings?: number; source?: string; }

const ManualMealPicker: React.FC<{ onSubmit: (ids: string[]) => void; onClose: () => void }> = ({ onSubmit, onClose }) => {
  const { user } = useAuth();
  const api = useApi();
  const [recipes, setRecipes] = useState<RecipeItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!user?.householdId) return;
      try { const data = await api.get<{ recipes: RecipeItem[]; count: number }>(`/v1/households/${user.householdId}/recipes`); setRecipes(data.recipes || []); }
      catch (err) { console.error('Failed to load recipes:', err); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line
  }, []);

  const toggleRecipe = (id: string) => { setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); };
  const filtered = recipes.filter((r) => r.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h3><BookOpen size={18} /> Choose Meals for Voting</h3><button className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <div className="recipes-search" style={{ marginBottom: '1rem' }}><Search size={16} /><input type="text" placeholder="Search recipes..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: '1rem', fontSize: '0.875rem' }}>{selected.size} recipe{selected.size !== 1 ? 's' : ''} selected — click to toggle</p>
          {loading ? (<div style={{ textAlign: 'center', padding: '2rem' }}><Loader size={24} className="spin" /></div>) : filtered.length === 0 ? (<p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>{recipes.length === 0 ? 'No recipes in your library yet.' : 'No recipes match your search.'}</p>) : (
            <div className="meals-grid">
              {filtered.map((recipe) => { const isSelected = selected.has(recipe.recipeId); return (
                <div key={recipe.recipeId} className={`meal-card ${isSelected ? 'finalized' : ''}`} style={{ cursor: 'pointer' }} onClick={() => toggleRecipe(recipe.recipeId)}>
                  <div className="meal-card-image">
                    {recipe.image ? (<img src={recipe.image} alt={recipe.title} loading="lazy" />) : (<div style={{ width: '100%', height: '100%', background: 'var(--color-bg-alt, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>🍽️</div>)}
                    {isSelected && <div className="finalize-check selected">✓</div>}
                  </div>
                  <div className="meal-card-body"><h3 className="meal-card-title">{recipe.title}</h3><div className="meal-card-meta">{(recipe.readyInMinutes ?? 0) > 0 && <span>⏱ {recipe.readyInMinutes} min</span>}{(recipe.servings ?? 0) > 0 && <span>👥 {recipe.servings} servings</span>}</div></div>
                </div>
              ); })}
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={() => onSubmit(Array.from(selected))} disabled={selected.size === 0}><Check size={16} /> Add {selected.size} Meal{selected.size !== 1 ? 's' : ''} for Voting</button></div>
      </div>
    </div>
  );
};

// ============================================================================
// Tab 2: Results — Vote results with member filters + finalization (primary only)
// ============================================================================
const ResultsTab: React.FC = () => {
  const { user } = useAuth();
  const api = useApi();
  const { household } = useHousehold();
  const weekId = getWeekId();
  const [voteSummaries, setVoteSummaries] = useState<VoteSummary[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [weekStatus, setWeekStatus] = useState<string>('none');
  const [finalizedMealIds, setFinalizedMealIds] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const householdId = user?.householdId;

  // Confirm modal state (replaces window.confirm)
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; action: () => void; label: string } | null>(null);

  const loadData = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    try {
      const [mealData, voteData] = await Promise.all([
        api.get<{ status: string; finalizedMealIds: string[] }>(`/v1/households/${householdId}/meal-plans/${weekId}`),
        api.get<{ votes: VoteSummary[] }>(`/v1/households/${householdId}/meal-plans/${weekId}/votes`),
      ]);
      setWeekStatus(mealData.status);
      setFinalizedMealIds(mealData.finalizedMealIds || []);
      setVoteSummaries(voteData.votes || []);
      if (mealData.status === 'finalized' && mealData.finalizedMealIds) setSelected(new Set(mealData.finalizedMealIds));
    } catch (err) { console.error('Failed to load results:', err); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [householdId, weekId]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSelect = (mealId: string) => { if (weekStatus === 'finalized') return; setSelected((prev) => { const next = new Set(prev); if (next.has(mealId)) next.delete(mealId); else next.add(mealId); return next; }); };

  const toggleMemberFilter = (userId: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleFinalize = () => {
    if (!householdId || selected.size === 0) return;
    setPendingConfirm({
      message: `Finalize ${selected.size} meal${selected.size !== 1 ? 's' : ''} for this week's grocery list?`,
      label: 'Finalize',
      action: async () => {
        setFinalizing(true);
        try { await api.post(`/v1/households/${householdId}/meal-plans/${weekId}/finalize`, { mealIds: Array.from(selected) }); await loadData(); }
        catch (err) { console.error('Finalize failed:', err); }
        finally { setFinalizing(false); }
      },
    });
  };

  const handleUndo = () => {
    if (!householdId) return;
    setPendingConfirm({
      message: 'Undo finalization? This will clear the grocery list.',
      label: 'Undo',
      action: async () => {
        setUndoing(true);
        try { await api.del(`/v1/households/${householdId}/meal-plans/${weekId}/finalize`); setSelected(new Set()); await loadData(); }
        catch (err) { console.error('Undo failed:', err); }
        finally { setUndoing(false); }
      },
    });
  };

  // Filter meals: when member filters active, show only meals where ANY selected member voted "up"
  const filteredMeals = selectedMembers.size === 0
    ? voteSummaries
    : voteSummaries.filter((meal) =>
        meal.voters.some((v) => selectedMembers.has(v.userId) && v.vote === 'up')
      );

  // Build member list from household data (all members: primary + member roles)
  const members = household?.members || [];

  if (loading) return (<div className="tab-panel" style={{ textAlign: 'center', padding: '3rem' }}><Loader size={24} className="spin" /><p>Loading vote results...</p></div>);
  if (voteSummaries.length === 0) return (<div className="tab-panel"><div className="empty-state"><ListChecks size={48} /><h3>No votes yet</h3><p>Generate meal selections first, then the family can vote.</p></div></div>);

  const isFinalized = weekStatus === 'finalized';
  return (
    <div className="tab-panel">
      <div className="tab-header">
        <div><h2>Vote Results</h2><p className="tab-subtitle">{formatWeekRange(weekId)} — {isFinalized ? 'Meals are finalized. Undo to make changes.' : 'Click meals to select them for the grocery list.'}</p></div>
        <div className="tab-header-action">
          {isFinalized ? (
            <button className="btn btn-secondary" onClick={handleUndo} disabled={undoing}>{undoing ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />} Undo Finalization</button>
          ) : (
            <button className="btn btn-primary" onClick={handleFinalize} disabled={finalizing || selected.size === 0}>{finalizing ? <Loader size={16} className="spin" /> : <Check size={16} />} Finalize {selected.size} Meal{selected.size !== 1 ? 's' : ''}</button>
          )}
        </div>
      </div>

      {/* Member filter bubbles */}
      {members.length > 0 && (
        <div className="results-member-filters">
          <span className="results-filter-label">Filter by member:</span>
          <div className="results-filter-bubbles">
            {members.map((m) => (
              <button
                key={m.userId}
                className={`results-filter-bubble ${selectedMembers.has(m.userId) ? 'active' : ''}`}
                onClick={() => toggleMemberFilter(m.userId)}
              >
                <span className="results-filter-bubble-initial">{(m.displayName || m.email || '?')[0].toUpperCase()}</span>
                {m.displayName || m.email}
              </button>
            ))}
            {selectedMembers.size > 0 && (
              <button className="results-filter-clear" onClick={() => setSelectedMembers(new Set())}>
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Meal tiles grid */}
      {filteredMeals.length === 0 ? (
        <div className="empty-state">
          <ListChecks size={48} />
          <h3>No matching meals</h3>
          <p>None of the selected members voted yes on any meals.</p>
        </div>
      ) : (
        <div className="meals-grid">
          {filteredMeals.map((meal) => {
            const isSelected = selected.has(meal.mealId);
            const upVoters = meal.voters.filter((v) => v.vote === 'up');
            const downVoters = meal.voters.filter((v) => v.vote === 'down');
            return (
              <div
                key={meal.mealId}
                className={`meal-card ${isSelected ? 'finalized' : ''} ${!isFinalized ? 'results-row--clickable' : ''}`}
                style={{ cursor: !isFinalized ? 'pointer' : 'default' }}
                onClick={() => toggleSelect(meal.mealId)}
              >
                <div className="meal-card-image">
                  {meal.recipeImage ? (
                    <img src={meal.recipeImage} alt={meal.recipeTitle} loading="lazy" />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: 'var(--color-bg-alt, #f3f4f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem' }}>🍽️</div>
                  )}
                  {isSelected && <div className="finalize-check selected">✓</div>}
                  <div className="results-vote-tally">
                    <span className="results-tally-up"><ThumbsUp size={12} /> {meal.upVotes}</span>
                    <span className="results-tally-down"><ThumbsDown size={12} /> {meal.downVotes}</span>
                  </div>
                </div>
                <div className="meal-card-body">
                  <h3 className="meal-card-title">{meal.recipeTitle}</h3>
                  <div className="meal-card-meta">
                    {(meal.readyInMinutes ?? 0) > 0 && <span>⏱ {meal.readyInMinutes} min</span>}
                    {(meal.servings ?? 0) > 0 && <span>👥 {meal.servings} servings</span>}
                  </div>
                  {/* Voter chips */}
                  {(upVoters.length > 0 || downVoters.length > 0) && (
                    <div className="results-voter-chips">
                      {upVoters.map((v) => (
                        <span key={v.userId} className="results-voter-chip results-voter-chip--up">
                          <ThumbsUp size={10} /> {v.displayName}
                        </span>
                      ))}
                      {downVoters.map((v) => (
                        <span key={v.userId} className="results-voter-chip results-voter-chip--down">
                          <ThumbsDown size={10} /> {v.displayName}
                        </span>
                      ))}
                    </div>
                  )}
                  {isSelected && (
                    <span className="results-badge" style={{ marginTop: '8px', display: 'inline-flex' }}>
                      <Check size={12} /> {isFinalized ? 'Finalized' : 'Selected'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm modal */}
      {pendingConfirm && (
        <ConfirmModal
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.label}
          onConfirm={() => { const action = pendingConfirm.action; setPendingConfirm(null); action(); }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
